import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://wingacademybrasil.com.br';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Componente de SEO reutilizável para definir meta tags por página.
 * - title: prefixado automaticamente com o nome da marca quando diferente da home
 * - description: meta description otimizada
 * - path: caminho da rota (ex: '/quiz') usado para canonical e og:url
 * - image: URL absoluta da imagem OG (opcional)
 * - noindex: bloqueia indexação da página
 * - jsonLd: array de schemas JSON-LD adicionais
 * - breadcrumbs: array [{ name, path }] — gera BreadcrumbList JSON-LD (home é adicionada automaticamente)
 */
const SEO = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = [],
  breadcrumbs = null,
}) => {
  const fullUrl = `${SITE_URL}${path}`;
  const buildTitle = () => {
    if (!title) return 'WING Academy Brasil — Mentoria Oficial Console Behringer WING';
    if (title.includes('WING Academy')) return title;
    return `${title} | WING Academy Brasil`;
  };
  const fullTitle = buildTitle();

  // Gera BreadcrumbList JSON-LD (Home é o primeiro item por padrão)
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: `${SITE_URL}/`,
          },
          ...breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: b.name,
            item: `${SITE_URL}${b.path}`,
          })),
        ],
      }
    : null;

  const allSchemas = breadcrumbSchema ? [breadcrumbSchema, ...jsonLd] : jsonLd;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      {description && <meta name="description" content={description} />}

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Hreflang (site é somente pt-BR) */}
      <link rel="alternate" hrefLang="pt-BR" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content="WING Academy Brasil" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="WING Academy Brasil — Mentoria Console Behringer WING" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD (breadcrumb + adicionais por página) */}
      {allSchemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
